import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { Logger } from '@utils/logger';
import { GlobalConfigService } from '@services/global-config-service';
import { ProfileService } from '@services/profile.service';
import {
  AnalyticsLogin,
  FtSignInInterface,
  SignInInterface,
  Skin,
  Skins,
} from '@shared/sign-in/sign-in.interface';
import { SignInService } from '@services/sign-in.service';
import { IUserProfile } from '@services/profile.interface';
import { SignInEvent } from '@frk/eds-components';

const logger = Logger.getLogger('SignInComponent');

@Component({
  selector: 'ft-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
})
export class SignInComponent extends AbstractBaseComponent implements OnInit {
  signIn: SignInInterface;
  public signInForm: FormGroup;
  public isSubmitButtonDisabled = false;
  profile: IUserProfile;
  hasSignIn = true;
  hasSignOut = true;
  loginOption = 'none';
  isLegacy = false;
  showLegacyContent = true;
  signInComponentContent: FtSignInInterface;
  skins: Skins;
  skin: Skin;
  leftPreTitle = 'Accounts';
  leftTitle = 'Access your account';
  leftContent =
    'Sign in or register for a personalized experience, ' +
    'offering exclusive resources and tools to help you better serve your clients.';

  // Page and Component inherited from AbstractBaseComponent
  @Input() inputParameters?: SignInInterface;

  constructor(
    private formBuilder: FormBuilder,
    private profileService: ProfileService,
    private globalConfigService: GlobalConfigService,
    private signInService: SignInService
  ) {
    super();
    const authOpt = this.globalConfigService.config.siteConfiguration?.site
      ?.authentication;
    this.loginOption = authOpt ? authOpt : 'none';
    this.signInForm = this.signInService.signInFormInit(this.formBuilder);
    this.skins = {
      basic: 'Basic',
      basicTwoCols: 'Basic 2 Columns',
      twoCols: 'Two Columns',
      threeCols: 'Three Columns',
    };
  }

  public ngOnInit() {
    this.setSignInComponentDefaultValues();
    this.setSignIn();
    this.profile = this.profileService.getUserProfile();
    this.signInService.setComponentParams(
      this.signIn,
      this.signInComponentContent
    );
    this.setLeftContentParams(this.signIn);
  }

  private setSignInComponentDefaultValues(): void {
    // Initiate default signInComponentContent object.
    // When component is created values are rewriting them with labels.
    // Method to set labels in in signInService setComponentParams.
    // Mapping labels here is not required.
    this.signInComponentContent = this.signInService.setDefaultComponent();
  }

  /**
   * Function to Set sign in labels.
   */
  public setSignIn() {
    try {
      const { SignIn } = this.component?.getModels();
      this.signIn = SignIn;
    } catch (e) {
      logger.debug('Model is undefined', e);
      // TODO: all labels need to come from data. we can't hardcode them
      const { SignIn } = require('./__fixtures/sign-in.fixture.json');
      this.signIn = SignIn;
    }
    this.skin = this.setSkin();
    this.isLegacy = this.setLegacy();
    this.hasSignOut = this.setSignOut();
  }

  /**
   * Set skin for component
   */
  private setSkin(): Skin {
    if (this.inputParameters?.skin) {
      return this.selectSkin(this.inputParameters.skin);
    }
    return this.selectSkin(this.signIn.skin);
  }

  /**
   * Set LM legacy for component
   */
  private setLegacy(): boolean {
    // Check Site configuration first
    if (
      this.globalConfigService.config.siteConfiguration.site
        ?.authenticationLegacy
    ) {
      // If there is component configuration to not display legacy use it as priority.
      if (this.signIn.legacy === false) {
        return false;
      }
      return this.globalConfigService.config.siteConfiguration.site
        .authenticationLegacy;
    }
    if (typeof this.inputParameters?.legacy !== 'undefined') {
      return this.inputParameters.legacy;
    }
    return this.signIn.legacy;
  }

  /**
   * Set signOut button for component
   */
  private setSignOut(): boolean {
    if (typeof this.inputParameters?.signOut !== 'undefined') {
      return this.inputParameters.signOut;
    }
    return this.signIn.signOut;
  }

  public onSubmit(signIn: SignInEvent) {
    logger.debug('SignInEvent', signIn);
    this.signInForm.value.userId = signIn.userName;
    this.signInForm.value.password = signIn.password;
    // Commented now according to NGC-8586
    // this.signInForm.value.rememberMe = signIn.rememberMe ? REMEMBER_ME_YES : REMEMBER_ME_NO;
    if (!signIn.userName && !signIn.password) {
      this.signInComponentContent.userName.invalid = true;
      this.signInComponentContent.password.invalid = true;
    } else if (!signIn.userName && signIn.password) {
      this.signInComponentContent.userName.invalid = true;
      this.signInComponentContent.password.invalid = false;
    } else if (signIn.userName && !signIn.password) {
      this.signInComponentContent.userName.invalid = false;
      this.signInComponentContent.password.invalid = true;
    } else {
      this.signInComponentContent.userName.invalid = false;
      this.signInComponentContent.password.invalid = false;
      this.isSubmitButtonDisabled = true;
      const submitObject: AnalyticsLogin = {
        signInForm: this.signInForm,
        analyticsKey: 'Banner',
      };
      this.signInService.onSubmit(submitObject);
    }
  }

  public signOut(): void {
    this.signInService.signOut();
  }
  /**
   * Set content form component params
   * @param componentParams - parameters form component
   */
  private setLeftContentParams(componentParams: SignInInterface): void {
    if (componentParams.signInLeftPreTitle !== '') {
      this.leftPreTitle = componentParams.signInLeftPreTitle;
    }
    if (componentParams.signInLeftTitle !== '') {
      this.leftTitle = componentParams.signInLeftTitle;
    }
    if (componentParams.signInLeftContent !== '') {
      this.leftContent = componentParams.signInLeftContent;
    }
    // TODO: Check possibility of using JCR document. This may impact embedding in other component functionality.
    // if (componentParams.document !== '') {
    //   this.leftContent = componentParams.document;
    // }
  }

  private selectSkin(componentSkin): Skin {
    switch (componentSkin) {
      case this.skins.basic:
        return {
          name: 'basic',
          offsets: {},
          columnNumbers: {},
          edsSpacing: 'md',
        };
      case this.skins.basicTwoCols:
        return {
          name: 'basicTwoCols',
          offsets: {},
          columnNumbers: { s: 4, m: 10, l: 10 },
          edsSpacing: 'md',
        };
      case this.skins.twoCols:
        return {
          name: 'twoCols',
          offsets: { l: 1 },
          columnNumbers: { s: 4, m: 4, l: 5 },
          addSectionColNumbers: { s: 4, m: 4, l: 4 },
          edsSpacing: 'xl',
        };
      case this.skins.threeCols:
        return {
          name: 'threeCols',
          offsets: {},
          addSectionColNumbers: {},
          columnNumbers: { s: 4, m: 4, l: 7 },
          edsSpacing: 'xl',
        };
      default:
        return { name: 'basic', offsets: {}, columnNumbers: {} };
    }
  }
}
